import { useEffect, useState } from "react";

// TYPES
import {
  ComponentProps,
  ForeignTaxDetailWithAddress,
  JuridicalShareholder,
  PhysicalShareholderFormik,
  ShareholderData,
  StepFourPJ,
} from "../../types";

// UTILS
import {
  genericToastifyError,
  isEmptyShareholder,
  isUncompletedShareholder,
} from "../../utils";
import {
  onUpdatePrimaryButton,
  onUpdateSecondaryButton,
  onUpdateTertiaryButton,
} from "../../reducers/buttonsReducer";
import {
  onUpdateActiveIndex,
  onUpdateIsFormOpen,
  onUpdateUtil,
} from "../../reducers/collectionReducer";
import { emptyPhysicalShareholder } from "../../constants";
import { sendStep } from "../../connectors/connectors";

// HOOKS
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import useForm from "../../hooks/useForm";
import useWindowWidth from "../../hooks/useWindowWidth";

// COMPONENTS
import { Icon } from "@allaria-tech/matter/Icon";
import { IconButton } from "@allaria-tech/matter/IconButton";
import { showToastMessage } from "@allaria-tech/matter/ToastMessage";
import Shareholder from "./Shareholder";

import styles from "./Shareholders.module.scss";
import { Feedback } from "@allaria-tech/matter/Feedback";
import useAlertFeedback from "../../hooks/useAlertFeedback";
import {
  addSendedStep,
  onUpdateCompletedStep,
  onUpdateFormData,
  onUpdateStep,
} from "../../reducers/onboardingDataReducer";
import {
  onUpdatePrimaryButtonModal,
  openModal,
} from "../../reducers/modalReducer";
import { getDraftStepsToSend, getForeignTaxDetails } from "../../app/utils";
import * as Sentry from "@sentry/react";
import { getCompanyData } from "../../utils/companies";

const NO_SHAREHOLDERS: ShareholderData[] = [];

const Shareholders = ({ formik }: ComponentProps) => {
  const { shareholdersContainer, shareholdersList } = styles;

  const [buttonIsLoading, setButtonIsLoading] = useState(false);

  const dispatch = useAppDispatch();

  const { handleBack } = useForm(formik);
  const discardChangesAlert = useAlertFeedback("discard-new-changes-shareholder");

  const { formValues, sendedSteps } = useAppSelector(
    (state) => state.onboardingData
  );

  const { isEditing, activeIndex, isFormOpen } = useAppSelector(
    (state) => state.collectionData
  );

  const { width } = useWindowWidth();
  const isMobile = width < 768;

  const shareholders: ShareholderData[] =
    formValues?.shareholders || NO_SHAREHOLDERS;

  const totalOwnership = shareholders?.reduce(
    (acc: number, shareholder: ShareholderData) =>
      acc + (Number(shareholder?.ownership) || 0),
    0
  );
  const hitOwnershipLimit = totalOwnership == 100;

  const addNewShareholder = () => {
    const newShareholders = [...shareholders, emptyPhysicalShareholder];
    dispatch(onUpdateFormData({ shareholders: newShareholders }));
  };

  const handleAddShareholder = () => {
    if (!shareholders) return;
    addNewShareholder();
    dispatch(onUpdateActiveIndex(shareholders?.length + 1));
    dispatch(onUpdateIsFormOpen(true));
  };

  const isDisableOnContinue =
    !shareholders ||
    shareholders.length === 0 ||
    shareholders.some(isUncompletedShareholder);

  const isDisabledAddShareholder =
    !shareholders ||
    hitOwnershipLimit ||
    shareholders.some(isUncompletedShareholder);

  // Their position in the collection is what names them and what the form is
  // keyed on, so it travels with them once the empty ones are left out
  const listedShareholders = shareholders
    .map((shareholder, i) => ({ shareholder, index: i + 1 }))
    .filter(({ shareholder }) => !isEmptyShareholder(shareholder));

  const onClickPrimaryButton = async () => {
    try {
      setButtonIsLoading(true);
      const shareholders = formValues?.shareholders;

      const parseShareholder = (shareholder: ShareholderData) => {
        if (!shareholder) return undefined;
        if (shareholder.personType === "JURIDICAL") {
          return parseJuridical(shareholder as JuridicalShareholder);
        } else return parsePhysical(shareholder as PhysicalShareholderFormik);
      };

      const parseJuridical = (shareholder: JuridicalShareholder) => {
        const { foreignTaxDetailBool, foreignTaxDetail, ...restShareholder } =
          shareholder;

        return {
          ...restShareholder,
          "@type": "JURIDICAL",
          ...getForeignTaxDetails(foreignTaxDetailBool ?? "NO", {
            idNumber: foreignTaxDetail?.idNumber ?? "",
            ...foreignTaxDetail?.address,
          } as {
            idNumber: string;
          } & ForeignTaxDetailWithAddress["address"]),
        };
      };

      const parsePhysical = (shareholder: PhysicalShareholderFormik) => {
        const {
          spouseName,
          spouseLastName,
          spouseCuil,
          foreignTaxDetailBool,
          foreignTaxDetail,
          pepDetails,
          pep,
          birthDate,
          ...restShareholder
        } = shareholder;

        const {
          fullAddress,
          stateSelect,
          stateText,
          ...addressWithoutFullAddress
        } = restShareholder.address;

        //MARK AS USED
        void fullAddress;
        void stateSelect;
        void stateText;

        const parsePep = (pep: string | undefined) => {
          if (pep === null || pep === undefined) {
            return "NONE";
          } else return pep;
        };

        const parsedPep = parsePep(pep);

        return {
          "@type": "PHYSICAL",
          ...restShareholder,
          pep: parsedPep,
          ...(parsedPep !== "NONE" && { pepDetails }),
          ...getForeignTaxDetails(foreignTaxDetailBool ?? "NO", {
            idNumber: foreignTaxDetail?.idNumber ?? "",
            ...foreignTaxDetail?.address,
          } as {
            idNumber: string;
          } & ForeignTaxDetailWithAddress["address"]),
          birthDate: birthDate,
          address: addressWithoutFullAddress,
          ...(spouseName &&
            spouseLastName && {
              spouse: {
                name: spouseName,
                lastName: spouseLastName,
                ...(spouseCuil && { cuit: spouseCuil }),
              },
            }),
        };
      };

      const shareholdersPhysicalsFiltered =
        shareholders
          ?.filter((share: ShareholderData) => share && share.taxId)
          .map(parseShareholder) ?? [];

      const step: StepFourPJ = {
        "@type": "4_JURIDICAL",
        shareholders: shareholdersPhysicalsFiltered ?? [],
      };

      await sendStep({
        stepId: 4,
        body: {
          companyType: getCompanyData().companyName,
          personType: "JURIDICAL", // It's always JURIDICAL
          email: formValues?.email || "",
          steps: getDraftStepsToSend(sendedSteps, step),
        },
      });

      dispatch(addSendedStep(step));
      dispatch(onUpdateCompletedStep(6));
      dispatch(onUpdateStep(7));
    } catch (error) {
      Sentry.captureException(error);
      showToastMessage(genericToastifyError, {
        containerId: "layoutPage",
        type: "ERROR",
      });
      throw new Error("Error at shareholders");
    } finally {
      setButtonIsLoading(false);
    }
  };

  // Mobile opens on the list, so it has no form waiting to be filled and an
  // empty placeholder would only disable "Cargar nuevo accionista"
  useEffect(() => {
    if (isMobile) return;

    if (!shareholders || shareholders.length === 0) {
      addNewShareholder();
    }
  }, [shareholders, isMobile]);

  // The step opens on the last shareholder loaded, the only one that can still
  // be pending, so a resumed onboarding lands on the form left unsaved
  useEffect(() => {
    dispatch(onUpdateActiveIndex(Math.max(shareholders.length, 1)));
    dispatch(onUpdateIsFormOpen(false));
  }, []);

  // Coming back from the form is the mobile counterpart of picking another
  // card: whatever was left unsaved is discarded, or it would sit in the
  // collection blocking the step with no card of its own to return through
  useEffect(() => {
    if (!isMobile || isFormOpen) return;

    const pendingIndex = shareholders.findIndex(isEmptyShareholder);
    if (pendingIndex === -1) return;

    const newShareholders = discardShareholderAt(pendingIndex);
    dispatch(onUpdateActiveIndex(Math.max(newShareholders.length, 1)));
  }, [isMobile, isFormOpen, shareholders]);

  // Mobile promotes "Cargar nuevo accionista" to primary and pushes the step's
  // own actions down a slot, so Continuar becomes secondary and Volver tertiary
  useEffect(() => {
    dispatch(
      onUpdateSecondaryButton(
        isMobile
          ? {
              text: "Continuar",
              disabled: isDisableOnContinue,
              isLoading: buttonIsLoading,
              onClick: onClickPrimaryButton,
              show: true,
            }
          : {
              text: "Volver",
              disabled: false,
              isLoading: false,
              onClick: handleBack,
              show: true,
            }
      )
    );
  }, [isMobile, shareholders, buttonIsLoading, isFormOpen]);

  // Handle primary button
  useEffect(() => {
    dispatch(
      onUpdatePrimaryButton(
        isMobile
          ? {
              text: "Cargar nuevo accionista",
              disabled: isDisabledAddShareholder,
              isLoading: false,
              onClick: handleAddShareholder,
              show: true,
            }
          : {
              text: "Continuar",
              disabled: isDisableOnContinue,
              isLoading: buttonIsLoading,
              onClick: onClickPrimaryButton,
              show: true,
            }
      )
    );
  }, [isMobile, shareholders, buttonIsLoading, isFormOpen]);

  useEffect(() => {
    dispatch(
      onUpdateTertiaryButton({
        text: "Volver",
        disabled: false,
        isLoading: false,
        onClick: handleBack,
        show: isMobile,
      })
    );
  }, [isMobile]);

  useEffect(() => {
    if (activeIndex && shareholders[activeIndex - 1]) {
      const activeShareholder = shareholders[activeIndex - 1];
      dispatch(onUpdateUtil(activeShareholder?.taxType));
    }
  }, [activeIndex, shareholders]);

  const discardShareholderAt = (index: number) => {
    const newShareholders = shareholders.filter((_, i) => i !== index);

    dispatch(onUpdateFormData({ shareholders: newShareholders }));
    formik?.setFieldValue("shareholders", newShareholders);

    return newShareholders;
  };

  // Leaving a shareholder that was never saved discards it, which is what the
  // alert warns about: having no card of its own, it would stay in the
  // collection blocking the step without any way back to its form
  const goToShareholder = (index: number) => {
    const pendingIndex = shareholders.findIndex(isEmptyShareholder);

    if (pendingIndex !== -1 && pendingIndex !== index - 1) {
      discardShareholderAt(pendingIndex);
    }

    dispatch(onUpdateActiveIndex(index));
    dispatch(onUpdateIsFormOpen(true));
  };

  const handleClickShareholder = (index: number) => {
    if (isEditing && activeIndex !== index) {
      dispatch(openModal({ name: "discard-new-changes-shareholder" }));
      dispatch(
        onUpdatePrimaryButtonModal({
          text: "Sí, eliminar",
          action: () => goToShareholder(index),
        })
      );
      return;
    }

    goToShareholder(index);
  };

  // Mobile shows one at a time: opening a shareholder hands the screen over to
  // its form, which comes back through the Page Header
  if (isMobile && isFormOpen) return null;

  return (
    <section className={shareholdersContainer}>
      <Feedback
        {...discardChangesAlert}
        type="WARNING"
        title="¿Descartar accionista?"
        description="Se descartarán los datos ingresados que no fueron guardados."
      />
      {/* A shareholder gets its card once it has a name, so the row shows up
      with the first one: until then the step opens straight on its form, and a
      new shareholder cannot be added anyway */}
      {listedShareholders.length > 0 && (
        <div className={`${shareholdersList} scrollbar`}>
          {listedShareholders.map(({ shareholder, index }) => (
            <Shareholder
              key={`shareholder-${index}`}
              shareholder={shareholder}
              index={index}
              isActive={activeIndex === index}
              isUncompleted={isUncompletedShareholder(shareholder)}
              showEditAction={isMobile}
              onClick={() => handleClickShareholder(index)}
            />
          ))}
          {!isMobile && (
            <IconButton
              variant="filled"
              size="small"
              aria-label="Agregar accionista"
              icon={<Icon name="plus" size="sm" />}
              onClick={handleAddShareholder}
              disabled={isDisabledAddShareholder}
            />
          )}
        </div>
      )}
    </section>
  );
};

export default Shareholders;
